import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
	History,
	Undo2,
	ArrowRight,
	CheckCircle2,
	XCircle,
	Loader2,
} from "lucide-react";
import { technicalAuditService } from "@/services/technicalAuditService";
import { useAuth } from "@/contexts/AuthContext";
import type { FixHistoryEntry } from "@/types/technicalAudit";

interface FixHistoryPanelProps {
	issueId: string;
}

function formatWhen(iso: string | null): string {
	if (!iso) return "";
	try {
		return new Date(iso).toLocaleString(undefined, {
			month: "short",
			day: "numeric",
			hour: "2-digit",
			minute: "2-digit",
		});
	} catch {
		return iso;
	}
}

function HistoryRow({ entry }: { entry: FixHistoryEntry }) {
	const isRevert = entry.action === "revert" || entry.action === "rollback";
	const failed = entry.success === false;
	const actionLabel =
		entry.action === "approve"
			? "Suggestion saved"
			: entry.action === "reject"
				? "Skipped"
				: isRevert
					? "Reverted"
					: entry.action === "verify"
						? "Verified"
						: "Applied";
	const actor = entry.applied_by || entry.actor_email || "system";
	const when = entry.applied_at || entry.created_at || null;
	return (
		<li className="flex items-start gap-3 py-2">
			<div
				className={`mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full ${
					failed
						? "bg-destructive/10"
						: isRevert
							? "bg-amber-100 dark:bg-amber-900/40"
							: "bg-teal-light"
				}`}
			>
				{failed ? (
					<XCircle className="h-3.5 w-3.5 text-destructive" />
				) : isRevert ? (
					<Undo2 className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />
				) : (
					<CheckCircle2 className="h-3.5 w-3.5 text-teal-dark" />
				)}
			</div>
			<div className="min-w-0 flex-1">
				<div className="flex items-center gap-2">
					<span className="text-xs font-medium capitalize text-foreground">
						{actionLabel}
					</span>
					{failed && (
						<Badge
							variant="outline"
							className="border-destructive/30 bg-destructive/5 px-1.5 py-0 text-[10px] text-destructive"
						>
							failed
						</Badge>
					)}
					{entry.reverted_at && !isRevert && (
						<Badge
							variant="outline"
							className="border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/40 px-1.5 py-0 text-[10px] text-amber-700 dark:text-amber-400"
						>
							rolled back
						</Badge>
					)}
				</div>
				<p
					className={`mt-0.5 break-words text-xs ${failed ? "text-destructive" : "text-muted-foreground"}`}
				>
					{entry.summary || entry.error || actionLabel}
				</p>
				<p className="mt-0.5 text-[11px] text-muted-foreground">
					{actor}
					{when ? ` · ${formatWhen(when)}` : ""}
				</p>
			</div>
		</li>
	);
}

/**
 * Fix history / audit log + rollback (fathom feedback §3 #4/#6). Shows the full
 * apply/revert timeline for an issue (who, when, before -> after) and — when the
 * latest applied fix captured a before-state — a one-click Revert that writes the
 * old value back to WordPress.
 */
export function FixHistoryPanel({ issueId }: FixHistoryPanelProps) {
	const { user } = useAuth();
	const queryClient = useQueryClient();
	const [confirming, setConfirming] = useState(false);

	const { data, isLoading } = useQuery({
		queryKey: ["technical-audit", "fix-history", issueId],
		queryFn: () => technicalAuditService.getFixHistory(issueId),
		enabled: Boolean(issueId),
	});

	const revert = useMutation({
		mutationFn: () =>
			technicalAuditService.revertFix(issueId, {
				applied_by: user?.email ?? undefined,
			}),
		onSuccess: (res) => {
			if (res.success) {
				// The revert writes WordPress directly (verified against stored meta),
				// but the public page can keep serving the pre-revert HTML for a few
				// minutes behind a page cache/CDN — WP Engine ignores unknown query
				// strings, so even a manual ?cb= check shows stale HTML until the
				// cache TTL expires. Say so, or a correct revert reads as "didn't
				// revert".
				toast.success(
					"Change reverted in WordPress. If the live page still shows the " +
						"old value, that's the site's page cache — it typically clears " +
						"within ~10 minutes.",
					{ duration: 8000 },
				);
				queryClient.invalidateQueries({
					queryKey: ["technical-audit", "fix-history", issueId],
				});
				queryClient.invalidateQueries({
					queryKey: ["technical-audit", "issues"],
				});
			} else {
				toast.error(res.error || "Revert failed.");
			}
			setConfirming(false);
		},
		onError: (err: unknown) => {
			toast.error(err instanceof Error ? err.message : "Revert failed.");
			setConfirming(false);
		},
	});

	const entries = data?.entries ?? [];
	// Nothing has ever been applied — don't clutter the drawer.
	if (!isLoading && entries.length === 0) return null;

	return (
		<section className="rounded-xl border border-border bg-muted/60 p-4">
			<div className="flex items-center justify-between gap-2">
				<h4 className="flex items-center gap-2 text-sm font-semibold text-foreground">
					<History className="h-4 w-4 text-muted-foreground" />
					Change history
				</h4>
				{data?.can_revert &&
					(confirming ? (
						<div className="flex items-center gap-2">
							<span className="text-xs text-muted-foreground">Roll back the last change?</span>
							<Button
								size="sm"
								variant="outline"
								onClick={() => setConfirming(false)}
								disabled={revert.isPending}
							>
								Cancel
							</Button>
							<Button
								size="sm"
								onClick={() => revert.mutate()}
								disabled={revert.isPending}
								className="gap-1.5 bg-amber-600 text-white hover:bg-amber-700"
							>
								{revert.isPending ? (
									<Loader2 className="h-3.5 w-3.5 animate-spin" />
								) : (
									<Undo2 className="h-3.5 w-3.5" />
								)}
								Confirm revert
							</Button>
						</div>
					) : (
						<Button
							size="sm"
							variant="outline"
							onClick={() => setConfirming(true)}
							className="gap-1.5 border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-400 hover:bg-amber-50"
						>
							<Undo2 className="h-3.5 w-3.5" />
							Revert last change
						</Button>
					))}
			</div>

			{isLoading ? (
				<div className="flex items-center gap-2 py-3 text-xs text-muted-foreground">
					<Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading history…
				</div>
			) : (
				<ul className="mt-2 divide-y divide-border">
					{entries.map((entry) => (
						<HistoryRow key={entry.id} entry={entry} />
					))}
				</ul>
			)}

			{/* A fail-closed apply wrote to WordPress and then reported failure
			    because it could not VERIFY the result (e.g. a missing-H1 insertion
			    that persisted but whose rendered readback came back unconfirmed).
			    The badge is red and Revert is offered, which reads as a
			    contradiction unless we say why: the page really was changed. */}
			{data?.can_revert && data?.fix_status === "failed" && (
				<p className="mt-2 flex items-start gap-1 text-[11px] text-amber-700 dark:text-amber-400">
					<ArrowRight className="mt-0.5 h-3 w-3 shrink-0" />
					This fix wrote to WordPress but could not confirm the result on the live
					page, so it is marked failed. The page was still changed — use Revert to
					undo that write.
				</p>
			)}

			{!data?.can_revert && entries.some((e) => e.action === "apply" && e.success) && (
				<p className="mt-2 flex items-center gap-1 text-[11px] text-muted-foreground">
					<ArrowRight className="h-3 w-3" />
					Reverted or non-revertible fixes can be re-applied or restored manually in
					WordPress.
				</p>
			)}
		</section>
	);
}
